import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useImpersonation } from './ImpersonationContext';
import { fetchFirm, ensureFirmForUser, updateFirm } from '../lib/db';
import { withTimeout, withRetry } from '../lib/fetchWithTimeout';

const FirmContext = createContext({ firm: null, loading: true, error: null, refresh: () => {} });

// Compute trial-related derived values from a firm row.
function trialInfo(firm) {
  if (!firm) return { subscriptionStatus: null, daysRemaining: 0, isTrialExpired: false, isTrial: false };

  const status = firm.subscription_status || 'trial';
  const trialEnds = firm.trial_ends_at ? new Date(firm.trial_ends_at) : null;
  const now = new Date();

  const msRemaining = trialEnds ? trialEnds - now : 0;
  const daysRemaining = Math.max(0, Math.ceil(msRemaining / (1000 * 60 * 60 * 24)));
  const isTrialExpired = status === 'trial' && trialEnds && now > trialEnds;
  const isTrial = status === 'trial';

  return { subscriptionStatus: status, daysRemaining, isTrialExpired, isTrial };
}

export const FirmProvider = ({ children }) => {
  const { user, userProfile, refreshProfile } = useAuth();
  const { impersonating }     = useImpersonation();
  const [firm, setFirm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);   // null | Error
  const [refreshSignal] = useState(0);

  // Track whether we've completed at least one successful load.
  const initialLoadDone = useRef(false);

  // Timestamp of the last visibility-triggered refresh. Used to enforce a
  // minimum 60-second cooldown so rapid tab-switching doesn't flood Supabase
  // with simultaneous query bursts.
  const lastVisibilityRefresh = useRef(0);

  // refresh(silent=false) — pass silent=true for background refreshes so
  // AppInner doesn't return null and unmount every page component.
  const refresh = useCallback(async (silent = false) => {
    if (!user) { setFirm(null); setLoading(false); setError(null); return; }

    if (!initialLoadDone.current && !silent) setLoading(true);
    if (!silent) setError(null);

    const impersonatingId = impersonating?.firm_id || null;

    try {
      let firmId = impersonatingId || userProfile?.firm_id;
      if (!firmId && !impersonatingId) {
        // retries=1 → 2 total attempts max (was 3, which could block for 90s)
        const ensureResult = await withRetry(() =>
          withTimeout(ensureFirmForUser(`${userProfile?.first_name || 'New'}'s Firm`)), 1
        );
        // Surface any firm-creation/linking error so the LoadError UI is shown
        // instead of silently proceeding with firmId=null (which leaves every
        // page rendering but every write silently failing).
        if (ensureResult?.error) {
          console.error('[FirmContext] ensureFirmForUser failed:', ensureResult.error);
          throw ensureResult.error;
        }
        firmId = ensureResult?.data?.firm_id || null;
        if (!firmId) {
          // ensureFirmForUser returned success but no firm_id — unusual; log and surface it
          console.error('[FirmContext] ensureFirmForUser returned no firm_id:', ensureResult);
          throw new Error('Could not link your account to a firm. Please refresh the page. If the problem persists, contact support.');
        }
        // The user was just promoted to Admin — refresh so userProfile.role
        // reflects 'Admin' immediately instead of showing 'Associate'.
        if (refreshProfile) await refreshProfile();
      }

      if (firmId) {
        const { data: loadedFirm, error: fetchErr } = await withRetry(() =>
          withTimeout(fetchFirm(firmId)), 1
        );
        if (fetchErr) throw fetchErr;
        // fetchFirm returned null — the firm row is missing or RLS is blocking it.
        // Surface a clear error so the user can see what's wrong.
        if (!loadedFirm) {
          console.error('[FirmContext] fetchFirm returned null for firmId:', firmId);
          throw new Error('Your firm could not be loaded (RLS or missing row). Run the repair migration in Supabase and refresh.');
        }

        // ── Trial-expired display state ──
        // Previously this block did a client-side
        // `updateFirm(..., { subscription_status: 'suspended' })` to
        // "auto-suspend" trial-expired firms. Two problems with that:
        //
        //  1. Migration 155's trigger blocks user-side writes to
        //     subscription_status (raises 42501). So this update was
        //     silently failing on every page load for trial-expired
        //     firms — surfacing as confusing errors in unrelated flows
        //     (e.g. the Close Firm dialog inherited the leaked 42501).
        //
        //  2. subscription_status is owned by Paystack now (and the
        //     suspension-detection logic in current_firm_id uses
        //     suspended_at, not subscription_status, for the actual
        //     access-blocking decision).
        //
        // The "suspended" UI is already driven by trialInfo() reading
        // trial_ends_at + subscription_status from the loaded firm row.
        // No mutation needed. AppWrapper's route guard routes a
        // trial-expired firm to SubscriptionSuspendedPage based purely
        // on the computed isTrialExpired flag; we don't need to write
        // anything to the firm row to make that work.
        //
        // If you want the database row's subscription_status to also
        // reflect "suspended" for reporting purposes, that's now
        // handled by a future background job / super-admin sweep,
        // NOT a client-side per-load write.

        setFirm(loadedFirm);
        setError(null);
      }
    } catch (err) {
      // On a background/silent refresh where we already have firm data,
      // swallow timeout errors silently — the user is mid-session and
      // the existing data is still valid.
      const isTimeout = err?.name === 'TimeoutError' || err?.message?.includes('timed out');
      if (silent && initialLoadDone.current && isTimeout) {
        console.warn('FirmContext silent refresh timed out — keeping existing data.');
        return;
      }
      console.error('FirmContext refresh error:', err);
      if (!silent || !initialLoadDone.current) {
        setError(err);
      }
    } finally {
      initialLoadDone.current = true;
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userProfile, impersonating?.firm_id]);

  // Run on mount and whenever user/userProfile change.
  useEffect(() => { refresh(); }, [refresh]);

  // When the browser tab becomes visible again, silently re-fetch firm-level
  // metadata only. Do not bump refreshSignal here: that remounts/reloads the
  // active page after every tab switch, and a sleeping browser can strand the
  // app on page-level loading if any foreground query stalls.
  useEffect(() => {
    const COOLDOWN_MS = 60_000; // 60 seconds
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityRefresh.current < COOLDOWN_MS) return;
      lastVisibilityRefresh.current = now;
      refresh(true);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const info = trialInfo(firm);

  return (
    <FirmContext.Provider value={{
      firm,
      firmId: firm?.id || userProfile?.firm_id,
      loading,
      error,
      refresh,
      refreshSignal,
      // Trial / subscription
      subscriptionStatus: info.subscriptionStatus,
      daysRemaining:      info.daysRemaining,
      isTrialExpired:     info.isTrialExpired,
      isTrial:            info.isTrial,
    }}>
      {children}
    </FirmContext.Provider>
  );
};

export const useFirm = () => useContext(FirmContext);
